import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useUpdateManagedDashboard } from "api/api-hooks/dashboards-hooks";
import { useGetCompanyUsers } from "api/api-hooks/user-hooks";
import { DashboardSimpleDto } from "api/model/dashboard/Dashboard";
import {
  ControllerMultiSelect,
  ControllerNumberInput,
  ControllerSwitchInput,
  ControllerTextInput,
} from "components/basic/controller-inputs/ControllerInputs";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { Button } from "primereact/button";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { z } from "zod";

const schema = z.object({
  id: z.number(),
  title: z
    .string({ message: T("word.required-fields") })
    .trim()
    .nonempty(),
  assignedUsers: z.array(z.string()),
  sortWeight: z.number().nonnegative(),
  companyShared: z.boolean(),
});

type EditDashboardFormProps = {
  dashboard: DashboardSimpleDto;
  onClose: () => void;
};

const EditDashboardForm: React.FC<EditDashboardFormProps> = ({ dashboard, onClose }) => {
  const company = useCurrentCompanyContext();
  const { data: users, isLoading } = useGetCompanyUsers(company?.companyReference!, {
    enabled: !!company?.companyReference,
  });
  const { mutate: updateDashboardMutation, isPending } = useUpdateManagedDashboard({
    onSuccess: onClose,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DashboardSimpleDto>({
    defaultValues: dashboard,
    resolver: zodResolver(schema),
  });

  const onSubmit = (formData: DashboardSimpleDto) => {
    updateDashboardMutation({ ...dashboard, ...formData });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={12} />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="assignedUsers"
          control={control}
          render={({ field }) => (
            <ControllerMultiSelect
              field={field}
              errors={errors}
              label={T("word.shared-for")}
              col={12}
              options={users ?? []}
              optionValue="username"
              optionLabel="username"
              maxSelectedLabels={1}
              filter
              filterIcon
              loading={isLoading}
              selectedItemsLabel={`{1} ${T("word.users")}`}
            />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="sortWeight"
          control={control}
          render={({ field }) => (
            <ControllerNumberInput field={field} errors={errors} label={T("word.sort-weight")} col={6} />
          )}
        />
        <Controller
          name="companyShared"
          control={control}
          render={({ field }) => (
            <ControllerSwitchInput field={field} errors={errors} label={T("word.public")} col={6} />
          )}
        />
      </div>
      <div className="mt- 2 flex flex-wrap gap-2 justify-content-end">
        <Button
          type="submit"
          label={T("action.update")}
          loading={isPending}
          className="p-button-rounded p-button-primary w-10rem"
        />
        <Button
          type="button"
          onClick={onClose}
          label={T("word.cancel")}
          rounded
          severity="secondary"
          outlined
          className="w-10rem"
        />
      </div>
    </form>
  );
};

export default EditDashboardForm;
